import {
  ShieldCheck,
  BadgeCheck,
  MapPin,
  FileText,
  Wrench,
  GraduationCap,
  Cpu,
  Globe,
  Mail,
  Phone,
  MapPin as LocationIcon,
} from "lucide-react";
import { Container } from "../../../shared";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Drone Planet News & Events API (separate backend from main REACT_APP_API_URL)
const DP_API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:6002";

const MAX_ITEMS = 6;

const infoData = {
  about: {
    title: "About Drone Planet",
    description:
      "Drone Planet is the official government portal for drone-related services in India, established under the Haryana Government's Drone City initiative and supported by the Ministry of Civil Aviation.",
    extra:
      "The portal provides a unified platform for drone registration, pilot certification, marketplace transactions, policy documents, and training programs — ensuring compliance with DGCA regulations.",
    cta: "Read More",
  },

  quickAccess: [
    { label: "Register Drone", icon: ShieldCheck },
    { label: "Get Pilot License", icon: BadgeCheck },
    { label: "Find DSK Centre", icon: MapPin },
    { label: "Apply for NOC", icon: FileText },
    { label: "Book Maintenance", icon: Wrench },
    { label: "Join Training", icon: GraduationCap },
    { label: "NPNT Compliance", icon: Cpu },
    { label: "Import/Export", icon: Globe },
  ],

  helpdesk: {
    phone: "+91 97735 71992",
    email: "support@droneplanet.in",
    email1: "sales@droneplanet.in",
    address:
      "Plot Number - 251, Ground Floor, Udyog Vihar, Phase IV, Sector 18, Gurugram, Haryana - 122015",
  },
};

const getDroneAnnouncements = async () => {
  try {
    const res = await axios.get(`${DP_API_BASE}/api/v1/news`, {
      params: { limit: MAX_ITEMS },
    });

    const rawData = res?.data?.data || [];

    const formatted = rawData.slice(0, MAX_ITEMS).map((item) => ({
      id: item._id,
      type: getTypeFromPriority(item.priority, item.title),
      date: item.publishedAt,
      text: item.title,
      link: item.url || "#",
    }));

    if (formatted.length === 0) {
      return getDummyAnnouncements();
    }

    return formatted;
  } catch (error) {
    console.error("Announcement API Error:", error);
    return getDummyAnnouncements();
  }
};

// Priority tiers: 100 DGCA/MoCA · 90 MoD · 80 ISRO/HAL/PMO · 70 Drone Shakti etc.
const getTypeFromPriority = (priority, title = "") => {
  if (priority >= 90) return "Important";
  if (priority >= 70) return "Official";
  if (priority >= 30) return "Update";
  return getType(title);
};

// 🛟 DUMMY FALLBACK (REALISTIC)
const getDummyAnnouncements = () => {
  return [
    {
      id: 1,
      type: "Important",
      date: new Date("2026-04-10"),
      text: "DGCA mandates re-verification of all registered drones by April 30, 2026",
      link: "#",
    },
    {
      id: 2,
      type: "Official",
      date: new Date("2026-04-05"),
      text: "Ministry of Civil Aviation releases updated UAV compliance framework",
      link: "#",
    },
    {
      id: 3,
      type: "Update",
      date: new Date("2026-04-02"),
      text: "New drone pilot certification guidelines announced for commercial operators",
      link: "#",
    },
    {
      id: 4,
      type: "Important",
      date: new Date("2026-03-28"),
      text: "Government introduces stricter drone airspace usage regulations in urban areas",
      link: "#",
    },
    {
      id: 5,
      type: "Update",
      date: new Date("2026-03-25"),
      text: "DigitalSky platform introduces faster approval process for UAV permissions",
      link: "#",
    },
  ];
};

// 🔥 TYPE CLASSIFICATION
const getType = (title) => {
  const t = title.toLowerCase();

  if (t.includes("regulation") || t.includes("rules")) return "Important";
  if (t.includes("policy") || t.includes("guideline")) return "Update";
  if (t.includes("dgca") || t.includes("government")) return "Official";

  return "News";
};

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 🛟 DUMMY FALLBACK
  const dummyAnnouncements = [
    {
      id: 1,
      type: "Important",
      date: "2026-04-10",
      text: "DGCA mandates re-verification of all registered drones by April 30, 2026",
      link: "#",
    },
    {
      id: 2,
      type: "Official",
      date: "2026-04-05",
      text: "Ministry of Civil Aviation releases updated UAV compliance framework",
      link: "#",
    },
    {
      id: 3,
      type: "Update",
      date: "2026-04-02",
      text: "New drone pilot certification guidelines announced",
      link: "#",
    },
    {
      id: 4,
      type: "Important",
      date: "2026-03-28",
      text: "Government introduces stricter drone airspace regulations",
      link: "#",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getDroneAnnouncements();

        const formatted = (data || []).slice(0, MAX_ITEMS).map((item, index) => ({
          id: item.id ?? index,
          type: item.type || getType(item.title || item.text || ""),
          date: item.pubDate || item.date,
          text: item.title || item.text,
          link: item.link || "#",
        }));

        if (!formatted.length) {
          setAnnouncements(dummyAnnouncements);
        } else {
          setAnnouncements(formatted);
        }
      } catch (err) {
        setError("Failed to load announcements");
        setAnnouncements(dummyAnnouncements);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getType = (title) => {
    const t = title.toLowerCase();

    if (t.includes("regulation") || t.includes("law")) return "Important";
    if (t.includes("policy") || t.includes("guideline")) return "Update";
    if (t.includes("dgca") || t.includes("government")) return "Official";

    return "News";
  };

  const getTypeColor = (type) => {
    if (type === "Important") return "text-red-500";
    if (type === "Update") return "text-yellow-500";
    if (type === "Official") return "text-green-600";
    return "text-blue-500";
  };

  // 🔥 SAFE DATE FORMATTER
  const formatDate = (date) => {
    const d = new Date(date);
    return isNaN(d.getTime())
      ? "Recently"
      : d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden h-full flex flex-col">
      <div className="bg-[#0B3C6D] text-white px-4 py-2 text-sm font-semibold shrink-0">
        Announcements
      </div>

      <div className="p-3 space-y-3 text-xs flex-1 overflow-y-auto max-h-[320px]">
        {loading && <p className="text-gray-400">Loading...</p>}

        {!loading &&
          announcements.map((item) => (
            <div key={item.id} className="flex gap-2">
              <div className="w-2 h-2 mt-1 rounded-full bg-[#0B3C6D]" />

              <div>
                <div className="flex gap-2 items-center">
                  <span
                    className={`text-[10px] font-semibold ${getTypeColor(
                      item.type,
                    )}`}
                  >
                    {item.type}
                  </span>

                  <span className="text-gray-400 text-[10px]">
                    {formatDate(item.date)}
                  </span>
                </div>

                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 leading-snug hover:text-[#0B3C6D]"
                >
                  {item.text}
                </a>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

const getDroneEvents = async () => {
  try {
    const res = await axios.get(`${DP_API_BASE}/api/v1/events`, {
      params: { limit: MAX_ITEMS },
    });

    const rawData = res?.data?.data || [];

    return rawData.slice(0, MAX_ITEMS).map((event) => {
      const hasDate = !!event.startDate;
      const dateObj = hasDate ? new Date(event.startDate) : null;
      const isValidDate = dateObj && !isNaN(dateObj.getTime());

      return {
        id: event._id,
        date: isValidDate ? dateObj.getDate() : "–",
        month: isValidDate
          ? dateObj.toLocaleString("default", { month: "short" })
          : "TBA",
        title: event.title,
        location:
          event.city ||
          event.venue ||
          event.state ||
          (event.eventType === "webinar" ? "Online" : "Location TBA"),
        tag: getTag(event.eventType || event.title || ""),
        link: event.url || "#",
      };
    });
  } catch (err) {
    console.error("Event API Error:", err);
    return [];
  }
};

const getTag = (value) => {
  const t = (value || "").toLowerCase();

  if (t === "expo" || t.includes("expo")) return "Expo";
  if (t === "summit" || t.includes("summit")) return "Summit";
  if (t === "workshop" || t.includes("workshop") || t.includes("training"))
    return "Workshop";
  if (t === "conference" || t.includes("conference")) return "Conference";
  if (t === "webinar" || t.includes("webinar")) return "Webinar";
  if (t === "hackathon" || t.includes("hackathon")) return "Hackathon";
  if (t === "meetup" || t.includes("meetup") || t.includes("networking"))
    return "Networking";

  return "Event";
};
// const getDroneEvents = async () => {
//   const res = await axios.get(BASE_URL, {
//     params: {
//       apikey: "pub_219059d898164ecd9a646f7220cee499",
//       q: "drone event OR UAV conference OR drone summit OR drone training India",
//       language: "en",
//       country: "in",
//     },
//   });

//   const rawData = res.data.results || [];

//   const today = new Date();

//   // 🔥 FILTER EVENT + FUTURE ONLY
//   const filtered = rawData.filter((item) => {
//     const text = (item.title + " " + item.description).toLowerCase();
//     const eventDate = new Date(item.pubDate);

//     const isEvent =
//       text.includes("event") ||
//       text.includes("summit") ||
//       text.includes("expo") ||
//       text.includes("conference") ||
//       text.includes("training") ||
//       text.includes("workshop");

//     const isUpcoming = eventDate >= today;

//     return isEvent && isUpcoming;
//   });

//   // 🔥 SORT (nearest upcoming first)
//   const sorted = filtered.sort(
//     (a, b) => new Date(a.pubDate) - new Date(b.pubDate),
//   );

//   // 🔥 FORMAT
//   const formatted = sorted.map((item, index) => {
//     const dateObj = new Date(item.pubDate);

//     return {
//       id: index,
//       date: dateObj.getDate(),
//       month: dateObj.toLocaleString("default", { month: "short" }),
//       title: item.title,
//       location: item.source_id || "India",
//       tag: getTag(item.title),
//     };
//   });

//   return formatted.slice(0, 3);
// };

// TAG LOGIC
// const getTag = (title) => {
//   const t = title.toLowerCase();

//   if (t.includes("summit") || t.includes("expo")) return "Summit";
//   if (t.includes("training") || t.includes("workshop")) return "Training";
//   if (t.includes("conference")) return "Conference";

//   return "Event";
// };

const Events = () => {
  const [events, setEvents] = useState([]);

  // useEffect(() => {
  //   const fetchEvents = async () => {
  //     const data = await getDroneEvents();
  //     setEvents(data);
  //   };

  //   fetchEvents();
  // }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getDroneEvents();
        console.log("event data: ", data);
        setEvents(data);
      } catch (err) {
        console.error(err);
        setEvents([]);
      }
    };

    fetchEvents();
  }, []);

return (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 h-full flex flex-col">
    {/* Header */}
    <div className="bg-[#0B3C6D] text-white px-4 py-2 text-sm font-semibold shrink-0">
      Upcoming Events
    </div>

    {/* Scroll Area */}
    <div className="p-3 space-y-3 text-xs flex-1 overflow-y-auto max-h-[320px]">
      

      {/* API Events */}

      {events.map((event) => (
        <a
          key={event.id}
          href={event.link || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex gap-2 hover:bg-gray-50 transition items-start no-underline rounded-lg p-2"
        >
          <div className="w-2 h-2 mt-1 rounded-full bg-[#0B3C6D] shrink-0" />

          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex gap-2 items-center">
              <span
                className={`text-[10px] font-semibold px-1.5 py-[1px] rounded ${getTagStyles(
                  event.tag
                )}`}
              >
                {event.tag}
              </span>

              <span className="text-gray-400 text-[10px]">
                {event.date === "–"
                  ? "TBA"
                  : `${event.date} ${event.month}`}
              </span>
            </div>

            <span className="text-gray-700 leading-snug hover:text-[#0B3C6D]">
              {event.title}
            </span>

            <span className="text-[10px] text-gray-400">
              {event.location}
            </span>
          </div>
        </a>
      ))}

      {events.length === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 font-medium">
            No upcoming drone events
          </p>

          <p className="text-xs text-gray-400 mt-1">
            Check back later for new conferences and trainings
          </p>
        </div>
      )}
    </div>
  </div>
);
};

/* Tag color system */
const getTagStyles = (tag) => {
  switch (tag?.toLowerCase()) {
    case "summit":
      return "bg-blue-100 text-blue-600";
    case "training":
      return "bg-green-100 text-green-600";
    case "expo":
      return "bg-orange-100 text-orange-600";
    case "networking":
      return "bg-purple-100 text-purple-600";
    case "workshop":
      return "bg-teal-100 text-teal-600";
    case "conference":
      return "bg-indigo-100 text-indigo-600";
    case "webinar":
      return "bg-cyan-100 text-cyan-600";
    case "hackathon":
      return "bg-pink-100 text-pink-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const InfoSection = ({ data = infoData }) => {
  const { about, quickAccess, helpdesk } = data || {};

  return (
    <section className="py-4">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ABOUT */}
          {/* <div className="bg-white rounded-lg overflow-hidden shadow-sm">
            <div className="bg-[#003580] text-white px-4 py-2 text-sm font-semibold">
              {about?.title}
            </div>

            <div className="p-4 text-sm text-gray-700 space-y-3">
              <p>{about?.description}</p>
              <p>{about?.extra}</p>

              <button className="text-[#0B3C6D] bg-transparent font-medium text-xs border-0">
                {about?.cta}
              </button>
            </div>
          </div> */}
          <Events />

          {/* QUICK ACCESS */}
          {/* <div className="bg-white rounded-lg overflow-hidden shadow-sm">
            <div className="bg-[#0B3C6D] text-white px-4 py-2 text-sm font-semibold">
              Quick Access
            </div>

            <div className="grid grid-cols-2 gap-2 p-3">
              {quickAccess?.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#0B3C6D] bg-[#EFF6FF] hover:bg-[#DBEAFE] transition border-0"
                  >
                    <Icon className="w-4 h-4 text-[#0B3C6D]" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div> */}
          <Announcements />

          {/* HELPDESK */}
          <div className="bg-white rounded-lg overflow-hidden shadow-sm h-full flex flex-col">
  <div className="bg-[#0B3C6D] text-white px-4 py-2 text-sm font-semibold shrink-0">
    Helpdesk & Contact
  </div>

  <div className="p-2 flex flex-col flex-1">
    <div className="space-y-2">
      {/* Phone */}
      <div className="bg-[#EFF6FF] rounded-md p-3">
        <p className="text-lg font-bold text-[#0B3C6D]">
          {helpdesk?.phone}
        </p>
        <p className="text-xs text-gray-500">
          Mon–Sat, 9:30 AM – 5:30 PM
        </p>
      </div>

      {/* Email */}
      <div className="flex items-center gap-2 text-gray-700">
        <Mail className="w-4 h-4 text-[#0B3C6D]" />
        For Sales: {helpdesk?.email1}
      </div>
      <div className="flex items-center gap-2 text-gray-700">
        <Mail className="w-4 h-4 text-[#0B3C6D]" />
        For Support: {helpdesk?.email}
      </div>
      {/* Address */}
      <div className="flex items-center gap-2 text-gray-700">
        <LocationIcon className="w-4 h-4 text-[#0B3C6D]" />
        {helpdesk?.address}
      </div>
    </div>

    {/* Support Button */}
    <div className="mt-auto pt-6">
      <Link
        to="/contact-page"
        className="block w-full text-center bg-[#0B3C6D] text-white py-2.5 rounded-md font-medium hover:bg-[#092f57] transition duration-200 no-underline"
      >
        Contact Support
      </Link>
    </div>
  </div>
</div>
        </div>
      </Container>
    </section>
  );
};

export default InfoSection;
